/**
 * PDF SSE Hook
 * 
 * React hook for receiving PDF export progress updates via Server-Sent Events.
 * Connects to supplement-specific PDF export channels and auto-closes on completion.
 * 
 * This hook is designed to be mounted at a high level (e.g., ProtectedRoute) so it persists
 * across navigation. It monitors all active PDF export mutations and maintains SSE connections
 * for each one, allowing users to navigate away while PDFs generate and still receive notifications.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useMutationState } from '@tanstack/react-query';
import { auth } from '@/lib/firebase';
import { env } from '@/config/env';
import { useToast } from '@/hooks/use-toast';

/**
 * PDF ready event data (matches backend PdfReadyEventData)
 */
export interface PdfReadyEvent {
  supplementId: string;
  companyId: string;
  claimId?: string;
  s3Key: string;
  downloadUrl: string;
  cached: boolean;
  timestamp: string;
}

/**
 * Hook to listen for PDF export completion across all active PDF generations
 * Monitors React Query mutation state to track which PDFs are being generated
 * Automatically downloads PDFs when ready and closes connections
 * 
 * This hook should be mounted at a high level (e.g., ProtectedRoute) so connections
 * persist even when navigating between pages.
 * 
 * IMPORTANT: SSE connections must stay open even after the mutation completes,
 * because the mutation completes when SQS request is sent, but we're waiting
 * for the Lambda webhook callback which can take 30-90 seconds.
 * 
 * @example
 * ```tsx
 * // In ProtectedRoute or App component
 * usePdfSSE();
 * ```
 */
export function usePdfSSE(): void {
  const { toast } = useToast();
  
  // Keep track of supplements that are waiting for PDF generation
  // This persists across mutation state changes
  const waitingSupplements = useRef(new Set<number>());
  const connections = useRef(new Map<number, AbortController>());

  // Track all PDF export mutations that have been initiated
  // Include both pending AND successful mutations (successful = SQS request sent)
  // We need to keep SSE connections open even after mutation succeeds
  // because we're waiting for the async Lambda callback
  const pdfExports = useMutationState({
    filters: { 
      mutationKey: ['supplements', 'exportPdf'],
      // Track both pending and successful mutations
      // Pending: mutation in flight
      // Success: SQS request sent, waiting for Lambda callback via SSE
    },
    select: (mutation) => {
      const supplementId = mutation.state.variables as number;
      const isPending = mutation.state.status === 'pending';
      const isSuccess = mutation.state.status === 'success';
      // Check if response indicates cached PDF (immediate download)
      const responseData = mutation.state.data as { cached?: boolean } | undefined;
      const isCached = isSuccess && responseData?.cached === true;
      
      return {
        supplementId,
        isPending,
        isSuccess,
        isCached,
        // Only track SSE if not cached (cached PDFs download immediately)
        shouldTrackSSE: (isPending || isSuccess) && !isCached,
      };
    },
  });

  const handlePdfReady = useCallback((event: PdfReadyEvent) => {
    console.log('[usePdfSSE] PDF ready:', event);

    const supplementId = parseInt(event.supplementId);
    
    // Remove from waiting list
    waitingSupplements.current.delete(supplementId);
    
    // Close SSE connection for this supplement
    const controller = connections.current.get(supplementId);
    if (controller) {
      controller.abort();
      connections.current.delete(supplementId);
    }

    // Show success notification
    toast({
      title: 'PDF Ready',
      description: `Supplement PDF is ready for download`,
      variant: 'default',
    });

    // Trigger download without opening in new tab
    // Fetch the PDF as a blob to force download behavior
    fetch(event.downloadUrl)
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `supplement-${event.supplementId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(err => {
        console.error('Failed to download PDF:', err);
        // Fallback to direct link if fetch fails
        window.open(event.downloadUrl, '_blank');
      });
  }, [toast]);

  // Create SSE connection for each PDF export that needs tracking
  useEffect(() => {
    // Add new exports to waiting list
    pdfExports.forEach(({ supplementId, shouldTrackSSE }) => {
      if (supplementId && shouldTrackSSE && !waitingSupplements.current.has(supplementId)) {
        console.log('[usePdfSSE] Adding supplement to waiting list:', supplementId);
        waitingSupplements.current.add(supplementId);
      }
    });

    // Remove exports that are cached (no longer need SSE)
    pdfExports.forEach(({ supplementId, isCached }) => {
      if (supplementId && isCached && waitingSupplements.current.has(supplementId)) {
        console.log('[usePdfSSE] Removing cached supplement from waiting list:', supplementId);
        waitingSupplements.current.delete(supplementId);
      }
    });

    const connectToSupplement = async (supplementId: number) => {
      try {
        // Get Firebase auth token
        const user = auth.currentUser;
        if (!user) {
          console.log('[usePdfSSE] Not authenticated, skipping connection');
          return;
        }

        const token = await user.getIdToken(true);

        // Connect to supplement-specific PDF export SSE channel
        const url = `${env.apiBaseUrl}/api/sse/supplements/${supplementId}/pdf-export`;
        console.log('[usePdfSSE] Connecting to:', url);

        const abortController = new AbortController();
        connections.current.set(supplementId, abortController);

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
          },
          signal: abortController.signal,
        });

        if (!response.ok) {
          console.error('[usePdfSSE] HTTP error:', response.status);
          connections.current.delete(supplementId);
          return;
        }

        console.log('[usePdfSSE] Connected successfully to supplement', supplementId);

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          console.error('[usePdfSSE] No response body');
          connections.current.delete(supplementId);
          return;
        }

        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log('[usePdfSSE] Stream closed for supplement', supplementId);
            connections.current.delete(supplementId);
            waitingSupplements.current.delete(supplementId);
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const event = JSON.parse(line.slice(6));

                // Only handle pdf_ready events
                if (event.type === 'pdf_ready') {
                  handlePdfReady(event.data);
                  // Connection will auto-close from backend after this event
                }
              } catch (error) {
                console.error('[usePdfSSE] Error parsing event:', error);
              }
            }
          }
        }
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          console.log('[usePdfSSE] Connection aborted for supplement', supplementId);
        } else {
          console.error('[usePdfSSE] Connection error for supplement', supplementId, error);
        }
        connections.current.delete(supplementId);
        waitingSupplements.current.delete(supplementId);
      }
    };

    // Connect to all supplements in the waiting list that don't have active connections
    waitingSupplements.current.forEach((supplementId) => {
      if (!connections.current.has(supplementId)) {
        console.log('[usePdfSSE] Starting SSE connection for supplement', supplementId);
        connectToSupplement(supplementId);
      }
    });

    // Cleanup: abort all connections when component unmounts
    // Capture refs in local variables to avoid stale closure warnings
    const connectionsToCleanup = connections.current;
    const supplementsToCleanup = waitingSupplements.current;
    
    return () => {
      connectionsToCleanup.forEach((controller) => controller.abort());
      connectionsToCleanup.clear();
      supplementsToCleanup.clear();
    };
  }, [pdfExports, handlePdfReady]);
}
